export const INVOKE_SUBFLOW_NODE_TYPE = "InvokeSubflowNode";
export const INVOKE_SUBFLOW_INPUTS_CONNECTOR = "inputs";

// Node types that render with the "subflow card" look on the canvas (workflow
// icon header, stacked-card shadow, no inline description). Both the V1
// connector-based node and the native V2 node share this presentation — the V2
// node still edits its config in the sidebar like any other V2 node, so this
// only affects the card chrome, not the V1 `inputs__<label>` binding helpers
// below (which remain V1-only).
export const SUBFLOW_CARD_NODE_TYPES = new Set<string>([
  "InvokeSubflowNode",
  "InvokeSubflowV2Node",
]);

export function isSubflowCardNode(nodeType: string | undefined): boolean {
  return nodeType !== undefined && SUBFLOW_CARD_NODE_TYPES.has(nodeType);
}

export function getSubflowWorkflowId(
  nodeType: string | undefined,
  nodeConfig: Record<string, unknown> | undefined
): string | undefined {
  if (!isSubflowCardNode(nodeType) || !nodeConfig) return undefined;

  const workflowId = nodeConfig.workflow_id;
  if (typeof workflowId === "string" && workflowId.length > 0) {
    return workflowId;
  }

  const subflowConfig = nodeConfig.subflow_config;
  if (
    subflowConfig &&
    typeof subflowConfig === "object" &&
    !Array.isArray(subflowConfig)
  ) {
    const legacyWorkflowId = (subflowConfig as Record<string, unknown>)
      .workflow_id;
    if (typeof legacyWorkflowId === "string" && legacyWorkflowId.length > 0) {
      return legacyWorkflowId;
    }
  }

  return undefined;
}

export function isInvokeSubflowExplicitInputConnector(
  nodeType: string | undefined,
  connectorName: string
): boolean {
  return (
    nodeType === INVOKE_SUBFLOW_NODE_TYPE &&
    connectorName === INVOKE_SUBFLOW_INPUTS_CONNECTOR
  );
}

export function shouldSkipAutoBindForConnector(
  nodeType: string | undefined,
  connectorName: string
): boolean {
  return isInvokeSubflowExplicitInputConnector(nodeType, connectorName);
}

export function invokeSubflowInputBindingKey(label: string): string {
  return `${INVOKE_SUBFLOW_INPUTS_CONNECTOR}__${label}`;
}

export function pruneOrphanedInvokeSubflowInputBindings(
  variableBindings: Record<string, string>,
  validKeys: string[]
): { bindings: Record<string, string>; changed: boolean } {
  const prefix = `${INVOKE_SUBFLOW_INPUTS_CONNECTOR}__`;
  const validSet = new Set(validKeys);
  let changed = false;
  const next = { ...variableBindings };

  for (const key of Object.keys(next)) {
    if (!key.startsWith(prefix)) continue;
    const label = key.slice(prefix.length);
    if (!validSet.has(label)) {
      delete next[key];
      changed = true;
    }
  }

  return { bindings: next, changed };
}
